import './BreakDialog.scss';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import moment, { Moment } from 'moment-timezone';
import * as _ from 'lodash';

import { Break as BreakInfo, ProviderSchedules } from '../../../redux/providers/types';
import { DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, IDropdownOption, MessageBar, MessageBarType, PrimaryButton, TextField } from '@fluentui/react';

import { AsyncData } from '../../../redux/core';
import { Market } from '../../../api/markets.types';
import { TimePicker } from '../../../components/core/TimePicker';

export interface BreakDialogProps {
  markets: Market[];
  isEdit: boolean;
  breakInfo?: BreakInfo;
  marketToProviderSchedules: { [marketCode: string]: AsyncData<ProviderSchedules[]> };
  createBreak?: (doctorId: string, startTime: number, endTime: number, type: string, MAId?: string) => void;
  editBreak?: (id: string, doctorId: string, startTime: number, endTime: number, type: string, MAId?: string) => void;
  deleteBreak?: (breakId: string) => void;
  uiDate: string;
}

export interface BreakDialogState {
  date: Moment;
  error?: string;
  selectedDoctor: string;
  selectedMA: string;
  breakType: string;
  duration: string;
  doctorList: { key: string; text: string }[];
  MAList: { key: string; text: string }[];
}

export class BreakDialog {
  public static show(options: BreakDialogProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(<BreakInternal {...options} close={close} />, dialogNode);
  }
}

class BreakInternal extends React.Component<BreakDialogProps & { close: () => void }, BreakDialogState> {
  constructor(props: BreakDialogProps | any) {
    super(props);
    this.state = {
      date: this.props.breakInfo
        ? moment.unix(this.props.breakInfo.startTime / 1000).tz(this.props.markets.find((m) => m.code === this.props.breakInfo!.marketCode)!.timeZone)
        : moment(this.props.uiDate),
      selectedDoctor: this.props.breakInfo ? this.props.breakInfo.doctorId : '',
      selectedMA: this.props.breakInfo ? this.props.breakInfo.assistantId : '',
      breakType: this.props.breakInfo ? this.props.breakInfo.type : 'Rest/Meal',
      duration: this.props.breakInfo ? ((this.props.breakInfo.endTime - this.props.breakInfo.startTime) / 60000).toString() : '30',
      doctorList: [],
      MAList: [],
    };
  }

  public componentDidMount() {
    let doctors: { key: string; text: string }[] = [];
    let MAs: { key: string; text: string }[] = [];
    for (const market in this.props.marketToProviderSchedules) {
      if (market && !this.props.marketToProviderSchedules[market].isFetching && this.props.marketToProviderSchedules[market].data) {
        doctors.push(
          ...this.props.marketToProviderSchedules[market]
            .data!.filter((p) => p.provider.providerType === 'DOCTOR')
            .map((d) => ({
              key: d.provider.id,
              text: `${d.provider.prefix} ${d.provider.firstName} ${d.provider.lastName}`,
            }))
        );
        MAs.push(
          ...this.props.marketToProviderSchedules[market]
            .data!.filter((p) => p.provider.providerType === 'MEDICAL_ASSISTANT')
            .map((d) => ({
              key: d.provider.id,
              text: `${d.provider.prefix} ${d.provider.firstName} ${d.provider.lastName}`,
            }))
        );
      }
    }
    doctors = _.sortBy(
      _.uniqBy(doctors, (i) => i.key),
      (i) => i.text
    );
    MAs = _.sortBy(
      _.uniqBy(MAs, (i) => i.key),
      (i) => i.text
    );

    this.setState({ doctorList: doctors, MAList: MAs });
  }

  public render(): JSX.Element {
    return (
      <Dialog
        hidden={false}
        modalProps={{
          isBlocking: true,
        }}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: this.props.isEdit ? 'Edit Break' : 'Create Break',
        }}
      >
        {this.state.error && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
            {this.state.error}
          </MessageBar>
        )}
        <div className="break-dialog-row flex flex-row">
          <div className="flex-grow flex-no-basis" style={{ marginRight: 10 }}>
            <TextField label="Date" value={this.props.uiDate} disabled={true} iconProps={{ iconName: 'Calendar' }} />
          </div>
          <div className="flex-grow flex-no-basis">
            <TimePicker value={this.state.date} onChange={(m) => this.setState({ date: m })} />
          </div>
        </div>

        <div className="break-dialog-row flex flex-row">
          <div className="flex-grow flex-no-basis" style={{ marginRight: 10 }}>
            <Dropdown
              label="Provider"
              selectedKey={this.state.selectedDoctor}
              onChange={this.changeDoctor}
              disabled={this.props.isEdit}
              placeholder="Select a Provider"
              options={this.state.doctorList || []}
            />
          </div>
          <div className="flex-grow flex-no-basis">
            <Dropdown label="MA (optional)" selectedKey={this.state.selectedMA} onChange={this.changeMA} placeholder="Select an MA" options={this.state.MAList || []} />
          </div>
        </div>

        <div className="break-dialog-row flex flex-row">
          <div className="flex-grow flex-no-basis" style={{ marginRight: 10 }}>
            <Dropdown
              label="Break Type"
              selectedKey={this.state.breakType}
              onChange={this.changeBreakType}
              placeholder="Select Break Type"
              options={[
                { key: 'Rest/Meal', text: 'Rest/Meal' },
                { key: 'Labs', text: 'Labs' },
                { key: 'Other', text: 'Other' },
              ]}
            />
          </div>
          <div className="flex-grow flex-no-basis">
            <Dropdown
              label="Duration"
              selectedKey={this.state.duration}
              onChange={this.changeDuration}
              placeholder="Select Break Duration"
              options={[
                { key: '15', text: '15 minutes' },
                { key: '30', text: '30 minutes' },
                { key: '45', text: '45 minutes' },
                { key: '60', text: '60 minutes' },
              ]}
            />
          </div>
        </div>

        <DialogFooter>
          {this.props.isEdit && (
            <div className="delete-break-button">
              <PrimaryButton onClick={this.onDelete} text="Delete" />
            </div>
          )}
          <PrimaryButton text={this.props.isEdit ? 'Save' : 'Create'} onClick={this.onSubmit} />
          <DefaultButton text="Cancel" onClick={this.props.close} />
        </DialogFooter>
      </Dialog>
    );
  }

  private changeDoctor = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    item && this.setState({ selectedDoctor: item.key as string });
  };

  private changeMA = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    item && this.setState({ selectedMA: item.key as string });
  };

  private changeBreakType = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    item && this.setState({ breakType: item.key as string });
  };

  private changeDuration = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    item && this.setState({ duration: item.key as string });
  };

  private onSubmit = () => {
    const startTime = this.state.date.valueOf();
    const endTime = startTime + parseInt(this.state.duration, 10) * 60 * 1000;
    if (this.props.isEdit && this.props.breakInfo && this.props.editBreak) {
      this.props.editBreak(this.props.breakInfo.id, this.state.selectedDoctor, startTime, endTime, this.state.breakType, this.state.selectedMA);
      this.props.close();
    } else if (this.props.createBreak) {
      this.props.createBreak(this.state.selectedDoctor, startTime, endTime, this.state.breakType, this.state.selectedMA);
      this.props.close();
    }
  };

  private onDelete = () => {
    if (this.props.deleteBreak && this.props.breakInfo) {
      this.props.deleteBreak(this.props.breakInfo.id);
      this.props.close();
    }
  };
}
